import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { format, isSameDay } from 'date-fns';

import Swal from 'sweetalert2';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';

import Dashboard from '~/layouts/Dashboard';
import { useAuth } from '~/hooks/auth';
import {
  ORDER_STATUS,
  ORDER_TYPES,
  ORDER_TYPES_CONSTS,
  PAYMENT_METHODS,
} from '~/utils/getEnums';
import { useOrders } from '~/hooks/orders';
import IOrder from '~/interfaces/IOrder';

import { useToast } from '~/hooks/toast';

import { DB } from '~/services/firebase';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

import { bff } from '~/services/bff';
import IClient from '~/interfaces/IClient';
import { handlePrint } from '~/utils/handlePrint';

const { REACT_APP_FILTER_ORDERS, REACT_APP_FIREBASE_PROJECT } = process.env;

const OrdersClient: React.FC = () => {
  const { addToast } = useToast();
  const { user, company } = useAuth();
  const { removeOrder } = useOrders();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);
  const [orders, setOrders] = useState<IOrder[]>([]);

  const params = useParams<{ id: string }>();
  const { state } = useLocation<IClient>();
  const { goBack } = useHistory();

  useEffect(() => {
    async function loadOrders() {
      const q = query(
        collection(DB, 'orders'),
        where('company_id', '==', company.uid),
        where('client_id', '==', params.id),
        limit(50),
      );
      const querySnapshot = await getDocs(q);

      const items: IOrder[] = [];

      querySnapshot.docs.forEach((item: any) => {
        const data = item.data() as IOrder;
        const created_at = new Date(data.created_at.seconds * 1000);

        const parseItem: IOrder = {
          ...data,
          uid: item.id,
          shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
          subtotal_formatted: getCurrencyFormatted(data.subtotal),
          total_formatted: getCurrencyFormatted(data.total),
          created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
        };

        items.push(parseItem);
      });

      setOrders(items);
    }

    if (params.id && company.uid) {
      loadOrders();
    }
  }, [user, company, params]);

  const filteredOrders = useMemo(() => {
    const filtered = orders.filter(order => {
      let result = true;

      if (REACT_APP_FILTER_ORDERS) {
        if (order.code) {
          result =
            order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) ||
            order.code.toLocaleLowerCase().includes(search.toLocaleLowerCase());
        } else {
          result = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        }
      }

      if (!REACT_APP_FILTER_ORDERS) {
        if (status === 'Pedidos de Hoje') {
          const today = new Date();
          const created_at = new Date(order.created_at.seconds * 1000);

          const isSameDate = isSameDay(today, created_at);

          if (order.code) {
            const orderName = order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            const orderCode = order.code
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            result = (orderName || orderCode) && isSameDate;
          } else {
            const orderName = order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            result = orderName && isSameDate;
          }
        } else if (order.code) {
          result =
            order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) ||
            order.code.toLocaleLowerCase().includes(search.toLocaleLowerCase());
        } else {
          result = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        }
      }

      return result;
    });

    return filtered;
  }, [search, orders, status]);

  const handleConfirmRemove = useCallback(
    async (id: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do pedido?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'orders', id));

        await bff.delete(`/orders/${REACT_APP_FIREBASE_PROJECT}`);

        removeOrder(id);

        addToast({
          type: 'success',
          description: 'Pedido foi removida com sucesso.',
        });
      }
    },
    [addToast, removeOrder],
  );

  const handlePrintCustom = useCallback(
    (order: IOrder) => {
      const parseOrder = {
        ...order,
        additionValue_formatted:
          order.additionValue && order.additionValue > 0
            ? getCurrencyFormatted(order.additionValue)
            : '',
      };

      handlePrint({ order: parseOrder, company });
    },
    [company],
  );

  return (
    <Dashboard containerMessage="col-xl-12">
      <div className="col-xl-12">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content d-flex">
              {/* <button
                type="button"
                onClick={goBack}
                className="btn btn-outline-light bg-white d-none d-sm-inline-flex mr-2"
              >
                <em className="icon ni ni-arrow-left" />
                <span>Voltar</span>
              </button> */}
              <Link
                to="/clients"
                className="btn btn-outline-light bg-white d-inline-flex mr-2"
              >
                <em className="icon ni ni-arrow-left" />
                <span>Voltar</span>
              </Link>
              <h3 className="nk-block-title page-title">
                {`Pedidos de ${state.name}`}
              </h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por código ou cliente"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>

                    {/* <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#all"
                                onClick={() => {
                                  setStatus('Todos');
                                  setShowFilter(false);
                                }}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#today"
                                onClick={() => {
                                  setStatus('Pedidos de Hoje');
                                  setShowFilter(false);
                                }}
                              >
                                <span>Pedidos de Hoje</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li> */}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row tb-card">
          {filteredOrders.length === 0 && (
            <div className="col-md-12">
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="d-flex justify-content-center">
                    Esse cliente ainda não realizou nenhum pedido.
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredOrders.map(item => (
            <div className="col-md-6 col-sm-6 mb-3" key={item.uid}>
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="team">
                    <Link
                      to={`/orders/details/${item.uid}`}
                      style={{ color: '#000', textTransform: 'none' }}
                    >
                      <div className="user-card user-card-s2">
                        {/* <div className="user-avatar lg bg-primary">
                        <span>{getInitialLettersOfName(item.name)}</span>
                        <div className="status dot dot-lg dot-success" />
                      </div> */}
                        <div>
                          <h6 className="mb-1">{item.name}</h6>
                          <span className="sub-text mb-2">{item.whatsapp}</span>

                          {item.order_type !== undefined &&
                            !item.table?.uid && (
                              <h6>{ORDER_TYPES[item.order_type]}</h6>
                            )}
                          {item.order_type === '2' && item.table !== null && (
                            <h6>
                              Mesa:
                              {item.table?.name}
                            </h6>
                          )}
                        </div>
                      </div>

                      <ul className="team-info-custom">
                        <li>
                          <span>Pedido</span>
                          <span>{`#${item.code}`}</span>
                        </li>
                        <li>
                          <span>Data e Hora</span>
                          <span className="text-right">
                            {item.created_at_formatted}
                          </span>
                        </li>
                        <li>
                          <span>Total</span>
                          <span>{item.total_formatted}</span>
                        </li>
                        <li>
                          <span>Status</span>
                          {item.status === '0' && (
                            <span className="badge badge-dot badge-primary">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '1' && (
                            <span className="badge badge-dot badge-warning">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '2' && (
                            <span className="badge badge-dot badge-info">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '3' && (
                            <span className="badge badge-dot badge-success">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '4' && (
                            <span className="badge badge-dot badge-danger">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                        </li>
                      </ul>
                    </Link>
                    <div className="team-view">
                      <div className="form-control-select">
                        <select
                          disabled
                          className="form-control"
                          value={item.status}
                          style={{ minWidth: 120 }}
                        >
                          <option value="">Selecione...</option>
                          <option value="0">NOVO PEDIDO</option>
                          <option value="1">PREPARANDO</option>
                          <option value="2">SAIU PARA ENTREGA</option>
                          <option value="3">CONCLUÍDO</option>
                          <option value="4">CANCELADO</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 ps-2 pe-2"
                        onClick={() => handlePrintCustom(item)}
                      >
                        <em className="icon ni ni-printer-fill" />
                        IMPRIMIR
                      </button>
                    </div>

                    <div className="team-view flex-column">
                      <div className="form-control-select">
                        <select
                          disabled
                          className="form-control"
                          value={
                            item.paymentMethod ??
                            item.orderPayments[0].payment_method_id
                          }
                        >
                          <option value="">Forma de Pagamento...</option>
                          <option value="0">DINHEIRO</option>
                          <option value="1">DÉBITO</option>
                          <option value="2">CRÉDITO</option>
                          <option value="3">PIX</option>
                        </select>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleConfirmRemove(item.uid || '')}
                        className="mt-2"
                      >
                        <em className="icon ni ni-trash-fill me-1" />
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="nk-block tb-status">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <table className="table table-orders">
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info tb-code">Código</th>
                      <th className="tb-odr-info tb-client">Cliente</th>
                      <th className="tb-odr-info tb-total">Total</th>
                      <th className="tb-odr-info tb-status">Status</th>
                      <th className="tb-odr-info tb-status">Forma Pagamento</th>
                      <th className="tb-odr-action" style={{ width: 225 }}>
                        &nbsp;
                      </th>
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {filteredOrders.map(order => (
                      <Fragment key={order.uid}>
                        {!order.chatbot && (
                          <tr className="tb-odr-item" key={order.uid}>
                            <td>
                              <Link to={`/orders/details/${order.uid}`}>
                                {`#${order.code}`}
                              </Link>
                              <br />
                              {order.created_at_formatted}
                            </td>
                            <td>
                              <strong style={{ textTransform: 'capitalize' }}>
                                {order.name}
                              </strong>
                              {order.coupon && order.coupon.uid && (
                                <span className="badge bg-secondary ml-2">
                                  {order.coupon.code}
                                </span>
                              )}
                              {order.order_type !== undefined &&
                                !order.table?.uid && (
                                  <span>
                                    <br />
                                    {ORDER_TYPES[order.order_type]}
                                  </span>
                                )}
                              {order.order_type === ORDER_TYPES_CONSTS.TABLE &&
                                order.table !== null && (
                                  <span>
                                    <br />
                                    {`Mesa: ${order.table?.name}`}
                                  </span>
                                )}
                            </td>
                            <td>{getCurrencyFormatted(order.total)}</td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  disabled
                                  className="form-control"
                                  value={order.status}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">NOVO PEDIDO</option>
                                  <option value="1">PREPARANDO</option>
                                  <option value="2">SAIU PARA ENTREGA</option>
                                  <option value="3">CONCLUÍDO</option>
                                  <option value="4">CANCELADO</option>
                                </select>
                              </div>
                            </td>

                            <td className="tb-status">
                              {order.orderPayments &&
                              order.orderPayments.length > 0 ? (
                                <p className="preview-item">
                                  {order.orderPayments.map((item, index) => (
                                    <span className="badge bg-secondary">
                                      {PAYMENT_METHODS[item.payment_method_id]}
                                    </span>
                                  ))}
                                </p>
                              ) : (
                                <div className="form-control-select">
                                  <select
                                    disabled
                                    className="form-control"
                                    value={
                                      order.paymentMethod ??
                                      order.orderPayments[0].payment_method_id
                                    }
                                  >
                                    <option value="">Selecione...</option>
                                    <option value="0">DINHEIRO</option>
                                    <option value="1">DÉBITO</option>
                                    <option value="2">CRÉDITO</option>
                                    <option value="3">PIX</option>
                                  </select>
                                </div>
                              )}
                            </td>

                            <td className="tb-odr-action">
                              {/* <div className="tb-odr-btns d-none d-sm-inline"> */}

                              <button
                                type="button"
                                onClick={() => {
                                  handleConfirmRemove(order.uid || '');
                                }}
                                className="mt-2"
                              >
                                <em className="icon ni ni-trash-fill me-1" />
                                Excluir
                              </button>
                              <button
                                type="button"
                                className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 pe-2"
                                onClick={() => handlePrintCustom(order)}
                              >
                                <em className="icon ni ni-printer-fill" />
                                IMPRIMIR
                              </button>
                            </td>
                          </tr>
                        )}

                        {order.chatbot && (
                          <tr className="tb-odr-item" key={order.uid}>
                            <td>
                              <Link to={`/orders/details/${order.uid}`}>
                                {`#${order.code}`}
                              </Link>
                              <br />
                              {order.created_at_formatted}
                            </td>
                            <td>
                              <strong style={{ textTransform: 'capitalize' }}>
                                {order.name}
                              </strong>
                              <span>
                                <br />
                                Chatbot Delivery
                              </span>
                            </td>
                            <td>
                              {order.total === 0 ? '-' : order.total_formatted}
                            </td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  disabled
                                  className="form-control"
                                  value={order.status}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">NOVO PEDIDO</option>
                                  <option value="1">PREPARANDO</option>
                                  <option value="2">SAIU PARA ENTREGA</option>
                                  <option value="3">CONCLUÍDO</option>
                                  <option value="4">CANCELADO</option>
                                </select>
                              </div>
                            </td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  disabled
                                  className="form-control"
                                  value={order.paymentMethod}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">DINHEIRO</option>
                                  <option value="1">DÉBITO</option>
                                  <option value="2">CRÉDITO</option>
                                  <option value="3">PIX</option>
                                </select>
                              </div>
                            </td>

                            <td className="tb-odr-action">
                              <button
                                type="button"
                                onClick={() => {
                                  handleConfirmRemove(order.uid || '');
                                }}
                                className="mt-2"
                              >
                                <em className="icon ni ni-trash-fill me-1" />
                                Excluir
                              </button>

                              <button
                                type="button"
                                className="btn btn-icon btn-white btn-dim btn-sm btn-primary pe-2 ms-2"
                                onClick={() => handlePrintCustom(order)}
                              >
                                <em className="icon ni ni-printer-fill" />
                                IMPRIMIR
                              </button>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default OrdersClient;
